import React, { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { Calendar, CheckCircle, X, ArrowsClockwise } from "@phosphor-icons/react";

/** ScheduleSection — drops onto TaskDetailPage when the task has been claimed.
 *  Client picks a 2-hour slot → contractor confirms (or counter-proposes). */
export default function ScheduleSection({ taskId, role, task, onUpdated }) {
    const [avail, setAvail] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const { data } = await http.get(`/tasks/${taskId}/availability`);
            setAvail(data);
        } catch (e) { setErr(formatApiError(e)); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);

    async function propose(date, window_) {
        if (!window.confirm(`Propose ${date} · ${slotLabel(window_)}?`)) return;
        setBusy(true); setErr("");
        try {
            await http.post(`/tasks/${taskId}/schedule/propose`, { date, window: window_ });
            await load();
            if (onUpdated) onUpdated();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function confirm() {
        setBusy(true); setErr("");
        try {
            await http.post(`/tasks/${taskId}/schedule/confirm`);
            await load();
            if (onUpdated) onUpdated();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function reject() {
        if (!window.confirm("Reject this time and let the other party propose another?")) return;
        setBusy(true); setErr("");
        try {
            await http.post(`/tasks/${taskId}/schedule/reject`);
            await load();
            if (onUpdated) onUpdated();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    if (!avail) return <div className="text-sm text-slate-500 py-6">Loading scheduling…</div>;

    const status = avail.schedule_status;
    const proposed = avail.proposed_slot;
    const confirmed = avail.confirmed_slot;
    const isClient = role !== "partner" && role !== "admin";

    return (
        <div data-testid="schedule-section" className="mt-8 bg-slate-900/50 border border-white/10 rounded-sm p-6">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="label-eyebrow text-gold">/ Scheduling</div>
                    <h2 className="font-serif text-2xl mt-1">Pick an appointment time</h2>
                </div>
                <div className="text-xs text-slate-400">
                    Contractor: <span className="text-slate-200">{avail.contractor?.name || "—"}</span>
                    {avail.contractor?.business_name ? ` · ${avail.contractor.business_name}` : null}
                </div>
            </div>

            {err && <div className="text-red-400 text-sm mb-3">{err}</div>}

            {/* Confirmed */}
            {status === "confirmed" && confirmed && (
                <div data-testid="schedule-confirmed" className="rounded-sm border border-emerald-500/40 bg-emerald-500/5 p-5">
                    <div className="flex items-start gap-3">
                        <CheckCircle size={22} weight="fill" className="text-emerald-400 mt-0.5" />
                        <div className="flex-1">
                            <div className="font-medium text-emerald-200">Appointment confirmed</div>
                            <div className="font-serif text-2xl text-white mt-1">{confirmed.date} · {confirmed.label || slotLabel(confirmed.window)}</div>
                            {avail.contractor?.phone && (
                                <div className="text-sm text-slate-300 mt-2">
                                    Contractor phone: <span className="text-gold">{avail.contractor.phone}</span>
                                </div>
                            )}
                        </div>
                        <button
                            data-testid="schedule-reschedule-btn"
                            onClick={reject}
                            disabled={busy}
                            className="text-xs px-3 py-1.5 rounded-sm border border-white/15 bg-white/5 text-slate-200 hover:bg-white/10 disabled:opacity-50 inline-flex items-center gap-1"
                        >
                            <ArrowsClockwise size={12} weight="bold" /> Reschedule
                        </button>
                    </div>
                </div>
            )}

            {/* Pending proposal */}
            {status === "proposed" && proposed && (
                <div data-testid="schedule-proposed" className="rounded-sm border border-gold/40 bg-gold/5 p-5">
                    <div className="flex items-start gap-3">
                        <Calendar size={22} weight="fill" className="text-gold mt-0.5" />
                        <div className="flex-1">
                            <div className="text-sm text-slate-300">
                                {proposed.proposed_by === (isClient ? "client" : "partner")
                                    ? "You proposed:"
                                    : `${proposed.proposed_by === "client" ? "Client" : "Contractor"} proposed:`}
                            </div>
                            <div className="font-serif text-2xl text-white mt-1">{proposed.date} · {proposed.label || slotLabel(proposed.window)}</div>
                        </div>
                    </div>
                    {/* Only show confirm if you're the OTHER party */}
                    {((proposed.proposed_by === "client" && !isClient) || (proposed.proposed_by === "partner" && isClient)) ? (
                        <div className="mt-4 flex gap-2 flex-wrap">
                            <button
                                data-testid="schedule-confirm-btn"
                                onClick={confirm} disabled={busy} className="btn-gold text-sm px-5 py-2 disabled:opacity-50"
                            >
                                <CheckCircle size={14} weight="fill" className="inline mr-1.5" /> Confirm this time
                            </button>
                            <button
                                data-testid="schedule-reject-btn"
                                onClick={reject} disabled={busy}
                                className="text-sm px-5 py-2 rounded-sm border border-white/15 bg-white/5 text-slate-200 hover:bg-white/10 disabled:opacity-50"
                            >
                                <X size={14} weight="bold" className="inline mr-1.5" /> Decline & propose another
                            </button>
                        </div>
                    ) : (
                        <div className="mt-3 text-xs text-slate-400">
                            Waiting on the {proposed.proposed_by === "client" ? "contractor" : "homeowner"} to confirm.
                            <button
                                data-testid="schedule-cancel-proposal-btn"
                                onClick={reject} disabled={busy}
                                className="ml-3 underline text-slate-300 hover:text-white"
                            >
                                Cancel proposal
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* No proposal yet → show calendar */}
            {status === "none" && (
                <>
                    {!avail.any_slots ? (
                        <div className="text-sm text-slate-400 italic py-3">
                            {isClient
                                ? "Your contractor hasn't set their weekly availability yet. We'll nudge them to do so."
                                : "You haven't set your weekly availability yet. Update it in your profile so clients can pick a slot."}
                        </div>
                    ) : (
                        <>
                            <div className="text-xs text-slate-400 mb-4">
                                Next 5 days · 2-hour windows · {isClient ? "Pick one and the contractor will confirm." : "Propose one and the homeowner will confirm."}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                {avail.days.map((d) => (
                                    <div key={d.date} data-testid={`day-col-${d.date}`} className="border border-white/10 rounded-sm p-3 bg-slate-950/40">
                                        <div className="text-xs text-slate-500 uppercase tracking-widest">{d.weekday_label}</div>
                                        <div className="mt-3 space-y-1.5">
                                            {d.available.length === 0 ? (
                                                <div className="text-xs text-slate-600 py-2">No slots</div>
                                            ) : d.available.map((s) => (
                                                <button
                                                    key={s.window}
                                                    type="button"
                                                    data-testid={`pick-slot-${d.date}-${s.window}`}
                                                    disabled={busy}
                                                    onClick={() => propose(d.date, s.window)}
                                                    className="w-full text-xs px-2 py-2 rounded-sm border border-white/10 text-slate-200 hover:border-gold/60 hover:bg-gold/10 hover:text-gold disabled:opacity-50 transition"
                                                >
                                                    {s.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function slotLabel(w) {
    if (!w) return "";
    const [a, b] = w.split("-").map((x) => parseInt(x, 10));
    const fmt = (h) => {
        const ap = h < 12 ? "AM" : "PM";
        const d = h <= 12 ? (h === 0 ? 12 : h) : h - 12;
        return `${d}:00 ${ap}`;
    };
    return `${fmt(a)} – ${fmt(b)}`;
}
